import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Phone, Loader2, NotebookPen } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import api from "@/lib/api";
import { schoolYear } from "@/components/PageHeader";

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-3">
      <span className="text-sm font-medium text-slate-500">{label}</span>
      <span className="text-sm font-semibold text-slate-800">{value || "—"}</span>
    </div>
  );
}

export default function ParentStudentInfo() {
  const { id } = useParams();
  const [p, setP] = useState(null);

  useEffect(() => { api.get(`/parent/students/${id}/profile`).then(({ data }) => setP(data)).catch(() => {}); }, [id]);

  if (!p) return <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const initials = `${(p.first_name || "")[0] || ""}${(p.last_name || "")[0] || ""}`.toUpperCase();

  return (
    <div data-testid="parent-student-info">
      <Card className="max-w-2xl border-slate-200 p-6 shadow-sm" data-testid="parent-profile-card">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-sky-100 font-heading text-2xl font-extrabold text-sky-700">{initials || "U"}</div>
          <div>
            <p className="font-heading text-xl font-bold text-slate-900">{p.first_name} {p.last_name}</p>
            <p className="text-sm text-slate-500">{p.email}</p>
            <span className="mt-1 inline-flex rounded-full bg-slate-900 px-2.5 py-0.5 text-xs font-semibold text-white">Team Member</span>
          </div>
        </div>
        <Separator className="my-5" />
        <div className="divide-y divide-slate-100">
          <Row label="District" value={p.district_name} />
          <Row label="Campus" value={p.campus_name ? `${p.campus_name}${p.campus_code ? ` (${p.campus_code})` : ""}` : null} />
          <Row label="Team" value={p.team_name ? `${p.team_name}${p.team_code ? ` (${p.team_code})` : ""}` : null} />
          <Row label="Active Year" value={schoolYear(p.active_year)} />
          <Row label="Graduation Year" value={p.graduation_year} />
          <Row label="Assignment Status" value={p.assignment_status} />
        </div>
      </Card>

      <Card className="mt-6 max-w-2xl border-slate-200 p-6 shadow-sm" data-testid="parent-groups-card">
        <h3 className="font-heading text-lg font-semibold text-slate-800">Groups</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {(p.groups || []).length === 0
            ? <span className="text-sm text-slate-400">Not assigned to any groups yet.</span>
            : p.groups.map((g) => <span key={g.id} className="rounded-full bg-violet-100 px-3 py-1 text-sm font-medium text-violet-700">{g.name}</span>)}
        </div>
      </Card>

      {p.bios_enabled && (
        <Card className="mt-6 max-w-2xl border-slate-200 p-6 shadow-sm" data-testid="parent-bio-card">
          <div className="mb-3 flex items-center gap-2">
            <NotebookPen className="h-5 w-5 text-slate-400" />
            <h3 className="font-heading text-lg font-semibold text-slate-800">Bio</h3>
          </div>
          {p.bio
            ? <p className="whitespace-pre-line text-sm leading-relaxed text-slate-700" data-testid="parent-bio-text">{p.bio}</p>
            : <p className="text-sm text-slate-400" data-testid="parent-bio-empty">{p.first_name} hasn't written a bio yet.</p>}
        </Card>
      )}

      <Card className="mt-6 max-w-2xl border-slate-200 p-6 shadow-sm" data-testid="parent-contact-card">
        <div className="mb-4 flex items-center gap-2">
          <Phone className="h-5 w-5 text-slate-400" />
          <h3 className="font-heading text-lg font-semibold text-slate-800">Contact &amp; emergency info</h3>
        </div>
        <div className="divide-y divide-slate-100">
          <Row label="Member phone" value={p.member_phone} />
          <Row label="Parent/Guardian name" value={[p.guardian_first_name, p.guardian_last_name].filter(Boolean).join(" ")} />
          <Row label="Parent/Guardian phone" value={p.guardian_phone} />
          <Row label="Parent/Guardian email" value={p.guardian_email} />
        </div>
      </Card>
    </div>
  );
}
